import React from "react";

import { ChevronDown, ChevronUp, Pencil, Save } from "lucide-react";



import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import SectionCard from "@/pages/appointmentDetails/SectionCard";

export default function AppointmentDiscoverySection({
  appointmentId,
  toast,

  selectedDiscoveryRunId,
  setSelectedDiscoveryRunId,
  discoveryRunOptions,

  hasProspectToggle,
  segmentedToggleWrapper,
  segmentedToggleBase,
  segmentedToggleActive,
  discoveryProspectIndex,
  primaryProspect,
  secondaryProspect,
  setDiscoveryProspectId,

  isDiscoveryEditing,
  setIsDiscoveryEditing,
  discoveryDirty,
  setDiscoveryDirty,
  discoveryEdits,
  setDiscoveryEdits,
  handleSaveDiscoveryOverrides,
  isDiscoverySaving,

  accentIconButtonClass,
  saveIconButtonClass,

  discovery,
  discoverySectionsOpen,
  setDiscoverySectionsOpen,
  discoveryProspect,
  discoveryFields,
  discoveryValuesByKey,
  formatDiscoveryValue,
}) {
  return (
    <SectionCard
      title="Discovery"
      right={
        <div className="flex items-center gap-3">
          <Select value={selectedDiscoveryRunId || ""} onValueChange={setSelectedDiscoveryRunId}>
            <SelectTrigger
              className="h-9 w-auto rounded-full border-transparent bg-indigo-500/20 px-4 text-xs text-indigo-100 shadow-none ring-0 hover:bg-indigo-500/30 focus:ring-0 focus-visible:ring-0 focus-visible:outline-none"
              data-testid="appointment-discovery-run-select"
            >
              <SelectValue placeholder="Run View" />
            </SelectTrigger>
            <SelectContent className="border-0 bg-indigo-500/20 text-indigo-100">
              {discoveryRunOptions.map((run) => (
                <SelectItem
                  key={run.id}
                  value={run.id}
                  className="focus:bg-indigo-500/30 data-[state=checked]:bg-indigo-500/30"
                  data-testid={`appointment-discovery-run-option-${run.id}`}
                >
                  {run.script_name || "Playbook"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasProspectToggle ? (
            <div className={segmentedToggleWrapper} data-testid="appointment-discovery-prospect-toggle">
              <button
                type="button"
                className={`${segmentedToggleBase} ${discoveryProspectIndex === 0 ? segmentedToggleActive : ""}`}
                onClick={() => primaryProspect?.id && setDiscoveryProspectId(primaryProspect.id)}
                data-testid="appointment-discovery-prospect-toggle-primary"
              >
                {primaryProspect?.name || "Prospect 1"}
              </button>
              <button
                type="button"
                className={`${segmentedToggleBase} ${discoveryProspectIndex === 1 ? segmentedToggleActive : ""}`}
                onClick={() => secondaryProspect?.id && setDiscoveryProspectId(secondaryProspect.id)}
                data-testid="appointment-discovery-prospect-toggle-secondary"
              >
                {secondaryProspect?.name || "Prospect 2"}
              </button>
            </div>
          ) : null}

          {isDiscoveryEditing && discoveryDirty ? (
            <Button
              type="button"
              variant="secondary"
              className={saveIconButtonClass}
              onClick={handleSaveDiscoveryOverrides}
              disabled={selectedDiscoveryRunId === "all" || isDiscoverySaving}
              data-testid="appointment-discovery-save-button"
            >
              <Save className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              className={accentIconButtonClass}
              onClick={async () => {
                if (isDiscoveryEditing && !discoveryDirty) {
                  setIsDiscoveryEditing(false);
                  setDiscoveryDirty(false);
                  setDiscoveryEdits({});
                  return;
                }
                if (selectedDiscoveryRunId === "all") {
                  toast({ title: "Select a specific run to edit", variant: "destructive" });
                  return;
                }

                try {
                  const oRes = await api.get(`/appointments/${appointmentId}/discovery_overrides`, {
                    params: { run_id: selectedDiscoveryRunId },
                  });
                  const items = Array.isArray(oRes.data?.items) ? oRes.data.items : [];
                  setDiscoveryEdits(
                    items.reduce((acc, it) => {
                      const key = `${it.subject_id || "household"}::${it.field_key}`;
                      acc[key] = Array.isArray(it.values) ? it.values : [];
                      return acc;
                    }, {})
                  );
                } catch {
                  // ignore
                }

                setIsDiscoveryEditing(true);
                setDiscoveryDirty(false);
              }}
              data-testid="appointment-discovery-edit-button"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      }
    >
      {discovery?.run_id ? (
        <div className="space-y-6" data-testid="appointment-discovery-section">
          <div>
            <button
              type="button"
              onClick={() => setDiscoverySectionsOpen((prev) => ({ ...prev, prospects: !prev.prospects }))}
              className="flex w-full items-center justify-between rounded-xl bg-white/5 px-4 py-3 pl-3 text-left ring-1 ring-white/10 border-l-4 border-indigo-500/60 hover:bg-white/10"
              data-testid="appointment-discovery-prospect-toggle"
            >
              <span className="text-sm font-semibold text-slate-100">Prospect Data</span>
              {discoverySectionsOpen.prospects ? (
                <ChevronUp className="h-4 w-4 text-slate-200/70" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-200/70" />
              )}
            </button>
            {discoverySectionsOpen.prospects ? (
              !discoveryProspect ? (
                <div
                  className="mt-3 text-sm text-slate-200/70"
                  data-testid="appointment-discovery-prospect-no-active"
                >
                  Select a prospect to view their fields.
                </div>
              ) : discoveryFields.prospects.length ? (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {discoveryFields.prospects.map((field) => {
                    const subjectId = discoveryProspect?.id;
                    const key = `${field.id}::${subjectId}`;
                    const values = discoveryValuesByKey.get(key) || [];
                    return (
                      <div
                        key={field.id}
                        className="rounded-xl bg-white/5 px-4 py-3 ring-1 ring-white/10"
                        data-testid={`appointment-discovery-prospect-${field.id}`}
                      >
                        <div
                          className="text-xs uppercase text-slate-200/60"
                          data-testid={`appointment-discovery-prospect-label-${field.id}`}
                        >
                          {field.label || "Field"}
                        </div>
                        <div
                          className="text-sm text-slate-100"
                          data-testid={`appointment-discovery-prospect-value-${field.id}`}
                        >
                          {isDiscoveryEditing ? (
                            <Input
                              value={(discoveryEdits[key] || []).join(", ") || values?.[0]?.value || ""}
                              onChange={(event) => {
                                setDiscoveryDirty(true);
                                setDiscoveryEdits((prev) => ({
                                  ...prev,
                                  [key]: [event.target.value],
                                }));
                              }}
                              placeholder="Enter value"
                              className="mt-2 h-9 bg-white/5 text-white"
                              data-testid={`appointment-discovery-prospect-input-${field.id}`}
                            />
                          ) : (
                            formatDiscoveryValue(field, values)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3 text-sm text-slate-200/70" data-testid="appointment-discovery-prospect-empty">
                  No prospect discovery fields found.
                </div>
              )
            ) : null}
          </div>

          <div>
            <button
              type="button"
              onClick={() => setDiscoverySectionsOpen((prev) => ({ ...prev, household: !prev.household }))}
              className="flex w-full items-center justify-between rounded-xl bg-white/5 px-4 py-3 pl-3 text-left ring-1 ring-white/10 border-l-4 border-indigo-500/60 hover:bg-white/10"
              data-testid="appointment-discovery-household-toggle"
            >
              <span className="text-sm font-semibold text-slate-100">Household Data</span>
              {discoverySectionsOpen.household ? (
                <ChevronUp className="h-4 w-4 text-slate-200/70" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-200/70" />
              )}
            </button>

            {discoverySectionsOpen.household ? (
              discoveryFields.household.length ? (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {discoveryFields.household.map((field) => {
                    const key = `${field.id}::household`;
                    const values = discoveryValuesByKey.get(key) || [];
                    return (
                      <div
                        key={field.id}
                        className="rounded-xl bg-white/5 px-4 py-3 ring-1 ring-white/10"
                        data-testid={`appointment-discovery-household-${field.id}`}
                      >
                        <div
                          className="text-xs uppercase text-slate-200/60"
                          data-testid={`appointment-discovery-household-label-${field.id}`}
                        >
                          {field.label || "Field"}
                        </div>
                        <div
                          className="text-sm text-slate-100"
                          data-testid={`appointment-discovery-household-value-${field.id}`}
                        >
                          {isDiscoveryEditing ? (
                            <Input
                              value={(discoveryEdits[key] || []).join(", ") || values?.[0]?.value || ""}
                              onChange={(event) => {
                                setDiscoveryDirty(true);
                                setDiscoveryEdits((prev) => ({
                                  ...prev,
                                  [key]: [event.target.value],
                                }));
                              }}
                              placeholder="Enter value"
                              className="mt-2 h-9 bg-white/5 text-white"
                              data-testid={`appointment-discovery-household-input-${field.id}`}
                            />
                          ) : (
                            formatDiscoveryValue(field, values)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3 text-sm text-slate-200/70" data-testid="appointment-discovery-household-empty">
                  No household discovery fields found.
                </div>
              )
            ) : null}
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-200/70" data-testid="appointment-discovery-empty">
          No discovery data captured for this appointment yet.
        </div>
      )}
    </SectionCard>
  );
}
